/**
 * Platform-owner (MCOS) billing panel, SUPERADMIN only.
 *
 * Three surfaces, all cross-clinic:
 *   • Overview  , active clinics, reports (PDFs), revenue, recent payments
 *   • Clinics   , manage clinics, per-clinic price, report counts, card status
 *   • Clinic    , detail + edit price/active, save a card, per-report ledger
 *
 * The card is entered through a Stripe Element, so raw card data goes straight
 * to Stripe and never touches our servers, we only ever hold a PaymentMethod id.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ConnectionsCard } from '../components/Connections';
import { getStripe, type StripeJs } from '../lib/stripe';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  LoadingRows,
  PageHeader,
  Pill,
  Spinner,
  StatCard,
  Table,
  Td,
  Th,
  Toggle,
  inputClass,
} from '../components/ui';
import { IconAnalytics, IconPatients, IconReports, IconAlert, IconCheck } from '../components/icons';

/* ------------------------------------------------------------------ *
 * Types + helpers
 * ------------------------------------------------------------------ */

interface CardInfo {
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
}
interface ClinicSummary {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  reportPriceCents: number;
  currency: string;
  reportCount: number;
  userCount: number;
  revenuePaidCents: number;
  outstandingCents: number;
  outstandingCount: number;
  card: CardInfo | null;
  createdAt: string;
}
type ChargeStatus = 'PENDING' | 'PAID' | 'FAILED' | 'SKIPPED' | 'REFUNDED';
interface Charge {
  id: string;
  tenantId: string;
  reportId: string;
  amountCents: number;
  currency: string;
  status: ChargeStatus;
  failureReason: string | null;
  createdAt: string;
  tenant?: { name: string; slug: string };
}
interface Analytics {
  clinics: { total: number; active: number };
  reports: { total: number; thisMonth: number };
  revenue: {
    paidCents: number;
    paidCount: number;
    thisMonthCents: number;
    outstandingCents: number;
    outstandingCount: number;
  };
  currency: string;
  stripeConfigured: boolean;
}
interface PlatformConfig {
  stripeConfigured: boolean;
  publishableKey: string | null;
  defaultPriceCents: number;
  currency: string;
}

function money(cents: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format((cents ?? 0) / 100);
}

function usePlatformConfig() {
  return useQuery({
    queryKey: ['platform', 'config'],
    queryFn: () => api.get<PlatformConfig>('/platform/config'),
    staleTime: 60_000,
  });
}

const STATUS_STYLE: Record<ChargeStatus, string> = {
  PAID: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  PENDING: 'bg-amber-50 text-amber-700 border border-amber-200',
  FAILED: 'bg-red-50 text-red-700 border border-red-200',
  SKIPPED: 'bg-ink/5 text-ink-muted border border-ink/10',
  REFUNDED: 'bg-ink/5 text-ink-muted border border-ink/10',
};

function StatusPill({ status }: { status: ChargeStatus }) {
  return <Pill className={STATUS_STYLE[status]}>{status.toLowerCase()}</Pill>;
}

function StripeBanner() {
  return (
    <div className="flex items-start gap-2 rounded-sm2 border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
      <span className="mt-0.5 shrink-0">
        <IconAlert size={16} />
      </span>
      <p>
        <strong>Stripe is not configured.</strong> Report usage is being tracked and charges are
        recorded as <em>skipped (unconfigured)</em>. Add your Stripe keys
        (<code>STRIPE_SECRET_KEY</code>, <code>STRIPE_PUBLISHABLE_KEY</code>) to enable real
        payments, nothing else changes.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Overview
 * ------------------------------------------------------------------ */

export function PlatformOverviewPage() {
  const analytics = useQuery({
    queryKey: ['platform', 'analytics'],
    queryFn: () => api.get<Analytics>('/platform/analytics'),
  });
  const charges = useQuery({
    queryKey: ['platform', 'charges'],
    queryFn: () => api.get<{ charges: Charge[] }>('/platform/charges'),
  });

  return (
    <div>
      <PageHeader
        title="Platform overview"
        subtitle="Report usage and payments across every clinic on MCOS."
      />

      {analytics.data && !analytics.data.stripeConfigured && (
        <div className="mb-5">
          <StripeBanner />
        </div>
      )}

      {analytics.isLoading ? (
        <LoadingRows rows={2} />
      ) : analytics.isError ? (
        <ErrorState error={analytics.error} retry={() => analytics.refetch()} />
      ) : analytics.data ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <StatCard
            label="Active clinics"
            value={analytics.data.clinics.active}
            deltaLabel={`${analytics.data.clinics.total} total`}
            tone="blue"
            icon={<IconPatients size={18} />}
          />
          <StatCard
            label="Reports (PDFs)"
            value={analytics.data.reports.total}
            deltaLabel={`${analytics.data.reports.thisMonth} this month`}
            tone="indigo"
            icon={<IconReports size={18} />}
          />
          <StatCard
            label="Revenue collected"
            value={money(analytics.data.revenue.paidCents, analytics.data.currency)}
            deltaLabel={`${money(analytics.data.revenue.thisMonthCents, analytics.data.currency)} this month`}
            tone="green"
            icon={<IconCheck size={18} />}
          />
          <StatCard
            label="Outstanding"
            value={money(analytics.data.revenue.outstandingCents, analytics.data.currency)}
            deltaLabel={`${analytics.data.revenue.outstandingCount} unpaid`}
            tone="rose"
            icon={<IconAnalytics size={18} />}
          />
        </div>
      ) : null}

      <Card title="Recent payments" padded={false}>
        {charges.isLoading ? (
          <div className="p-5">
            <LoadingRows rows={4} />
          </div>
        ) : charges.data && charges.data.charges.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <Th>Clinic</Th>
                <Th>Amount</Th>
                <Th>Status</Th>
                <Th align="right">When</Th>
              </tr>
            </thead>
            <tbody>
              {charges.data.charges.slice(0, 12).map((c) => (
                <tr key={c.id}>
                  <Td>{c.tenant?.name ?? c.tenantId}</Td>
                  <Td>{money(c.amountCents, c.currency)}</Td>
                  <Td>
                    <StatusPill status={c.status} />
                  </Td>
                  <Td align="right" className="text-ink-muted">
                    {new Date(c.createdAt).toLocaleDateString()}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <div className="p-5">
            <EmptyState title="No charges yet" body="Charges appear here as clinics generate reports." />
          </div>
        )}
      </Card>
      <div className="mt-5">
        <ConnectionsCard path="/platform/connections" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Clinics list
 * ------------------------------------------------------------------ */

export function PlatformClinicsPage() {
  const config = usePlatformConfig();
  const clinics = useQuery({
    queryKey: ['platform', 'clinics'],
    queryFn: () => api.get<{ clinics: ClinicSummary[] }>('/platform/clinics'),
  });
  const [showAdd, setShowAdd] = useState(false);

  return (
    <div>
      <PageHeader
        title="Clinics"
        subtitle="Every clinic on MCOS, its report volume and billing."
        actions={
          <Button onClick={() => setShowAdd((v) => !v)}>{showAdd ? 'Close' : 'Add clinic'}</Button>
        }
      />

      {showAdd && (
        <div className="mb-5">
          <AddClinicForm
            defaultPriceCents={config.data?.defaultPriceCents ?? 2500}
            onDone={() => {
              setShowAdd(false);
              clinics.refetch();
            }}
          />
        </div>
      )}

      <Card padded={false}>
        {clinics.isLoading ? (
          <div className="p-5">
            <LoadingRows rows={5} />
          </div>
        ) : clinics.isError ? (
          <div className="p-5">
            <ErrorState error={clinics.error} retry={() => clinics.refetch()} />
          </div>
        ) : clinics.data && clinics.data.clinics.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <Th>Clinic</Th>
                <Th align="right">Reports</Th>
                <Th align="right">Price / report</Th>
                <Th align="right">Collected</Th>
                <Th align="right">Outstanding</Th>
                <Th>Card</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {clinics.data.clinics.map((c) => (
                <tr key={c.id} className="hover:bg-ink/5">
                  <Td>
                    <Link to={`/platform/clinics/${c.id}`} className="font-medium text-ink hover:underline">
                      {c.name}
                    </Link>
                    <span className="block text-[11px] text-ink-muted">{c.slug}</span>
                  </Td>
                  <Td align="right">{c.reportCount}</Td>
                  <Td align="right">{money(c.reportPriceCents, c.currency)}</Td>
                  <Td align="right">{money(c.revenuePaidCents, c.currency)}</Td>
                  <Td align="right">
                    {c.outstandingCents > 0 ? (
                      <span className="text-amber-700">{money(c.outstandingCents, c.currency)}</span>
                    ) : (
                      <span className="text-ink-muted">-</span>
                    )}
                  </Td>
                  <Td>
                    {c.card ? (
                      <span className="text-ink-muted">
                        {c.card.brand} ···· {c.card.last4}
                      </span>
                    ) : (
                      <span className="text-ink-muted">None</span>
                    )}
                  </Td>
                  <Td>
                    <Pill
                      className={
                        c.active
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-ink/5 text-ink-muted border border-ink/10'
                      }
                    >
                      {c.active ? 'active' : 'inactive'}
                    </Pill>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <div className="p-5">
            <EmptyState title="No clinics yet" body="Add your first clinic to start billing." />
          </div>
        )}
      </Card>
    </div>
  );
}

function AddClinicForm({
  defaultPriceCents,
  onDone,
}: {
  defaultPriceCents: number;
  onDone: () => void;
}) {
  const [name, setName] = useState('');
  const [priceDollars, setPriceDollars] = useState((defaultPriceCents / 100).toFixed(2));
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post('/platform/clinics', {
        name: name.trim(),
        reportPriceCents: Math.round(parseFloat(priceDollars || '0') * 100),
        admin:
          adminEmail.trim() && adminPassword
            ? { name: adminName.trim() || `${name.trim()} Admin`, email: adminEmail.trim(), password: adminPassword }
            : undefined,
      }),
    onSuccess: onDone,
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Could not create clinic'),
  });

  return (
    <Card title="Add a clinic">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Clinic name" required>
          <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Vitality Wellness" />
        </Field>
        <Field label="Price per report (USD)" required hint="Charged to this clinic's card for each report.">
          <input
            className={inputClass}
            value={priceDollars}
            onChange={(e) => setPriceDollars(e.target.value)}
            inputMode="decimal"
          />
        </Field>
      </div>
      <p className="mt-4 mb-2 text-xs font-semibold text-ink-muted uppercase tracking-wide">
        Initial clinic admin (optional)
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label="Admin name">
          <input className={inputClass} value={adminName} onChange={(e) => setAdminName(e.target.value)} />
        </Field>
        <Field label="Admin email">
          <input className={inputClass} value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} type="email" />
        </Field>
        <Field label="Temp password" hint="At least 10 characters.">
          <input className={inputClass} value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} type="text" />
        </Field>
      </div>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      <div className="mt-4 flex justify-end">
        <Button onClick={() => create.mutate()} loading={create.isPending} disabled={name.trim().length < 2}>
          Create clinic
        </Button>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * Clinic detail
 * ------------------------------------------------------------------ */

export function PlatformClinicDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const detail = useQuery({
    queryKey: ['platform', 'clinic', id],
    queryFn: () => api.get<{ clinic: ClinicSummary; charges: Charge[] }>(`/platform/clinics/${id}`),
    enabled: Boolean(id),
  });

  const patch = useMutation({
    mutationFn: (body: Partial<{ active: boolean; reportPriceCents: number }>) =>
      api.patch(`/platform/clinics/${id}`, body),
    onSuccess: () => {
      detail.refetch();
      qc.invalidateQueries({ queryKey: ['platform', 'clinics'] });
    },
  });

  const retry = useMutation({
    mutationFn: (chargeId: string) => api.post(`/platform/charges/${chargeId}/retry`),
    onSuccess: () => detail.refetch(),
  });

  const [priceDollars, setPriceDollars] = useState<string | null>(null);

  if (detail.isLoading) return <LoadingRows rows={4} />;
  if (detail.isError || !detail.data)
    return <ErrorState error={detail.error} retry={() => detail.refetch()} />;

  const clinic = detail.data.clinic;
  const priceValue = priceDollars ?? (clinic.reportPriceCents / 100).toFixed(2);

  return (
    <div>
      <PageHeader
        title={clinic.name}
        subtitle={`${clinic.slug} · ${clinic.reportCount} reports · ${clinic.userCount} users`}
        actions={
          <Button variant="ghost" onClick={() => navigate('/platform/clinics')}>
            ← All clinics
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Billing settings */}
        <Card title="Billing" className="lg:col-span-1">
          <Field label="Price per report (USD)">
            <div className="flex gap-2">
              <input
                className={inputClass}
                value={priceValue}
                inputMode="decimal"
                onChange={(e) => setPriceDollars(e.target.value)}
              />
              <Button
                variant="secondary"
                onClick={() => patch.mutate({ reportPriceCents: Math.round(parseFloat(priceValue || '0') * 100) })}
                loading={patch.isPending}
              >
                Save
              </Button>
            </div>
          </Field>

          <div className="mt-4">
            <Toggle
              checked={clinic.active}
              onChange={(next) => patch.mutate({ active: next })}
              label="Clinic active"
              description="Inactive clinics keep their data but cannot generate new reports."
            />
          </div>

          <div className="mt-5 border-t border-ink/5 pt-4">
            <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide mb-2">
              Payment method
            </p>
            <PaymentMethodSection clinic={clinic} onChange={() => detail.refetch()} />
          </div>
        </Card>

        {/* Revenue + ledger */}
        <div className="lg:col-span-2 space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <StatCard label="Reports" value={clinic.reportCount} tone="indigo" icon={<IconReports size={18} />} />
            <StatCard label="Collected" value={money(clinic.revenuePaidCents, clinic.currency)} tone="green" icon={<IconCheck size={18} />} />
            <StatCard
              label="Outstanding"
              value={money(clinic.outstandingCents, clinic.currency)}
              deltaLabel={`${clinic.outstandingCount} unpaid`}
              tone="rose"
              icon={<IconAlert size={18} />}
            />
          </div>

          <Card title="Charge ledger" subtitle="One row per report generated." padded={false}>
            {detail.data.charges.length === 0 ? (
              <div className="p-5">
                <EmptyState title="No charges yet" body="Charges appear as this clinic generates reports." />
              </div>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Date</Th>
                    <Th align="right">Amount</Th>
                    <Th>Status</Th>
                    <Th>Detail</Th>
                    <Th align="right">Action</Th>
                  </tr>
                </thead>
                <tbody>
                  {detail.data.charges.map((c) => (
                    <tr key={c.id}>
                      <Td className="text-ink-muted">{new Date(c.createdAt).toLocaleString()}</Td>
                      <Td align="right">{money(c.amountCents, c.currency)}</Td>
                      <Td>
                        <StatusPill status={c.status} />
                      </Td>
                      <Td className="text-ink-muted text-xs">{c.failureReason ?? '-'}</Td>
                      <Td align="right">
                        {(c.status === 'FAILED' || c.status === 'PENDING') && (
                          <Button
                            size="sm"
                            variant="secondary"
                            loading={retry.isPending && retry.variables === c.id}
                            onClick={() => retry.mutate(c.id)}
                          >
                            Retry
                          </Button>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Payment method, Stripe Elements card entry
 * ------------------------------------------------------------------ */

function PaymentMethodSection({ clinic, onChange }: { clinic: ClinicSummary; onChange: () => void }) {
  const config = usePlatformConfig();
  const [editing, setEditing] = useState(false);

  const remove = useMutation({
    mutationFn: () => api.delete(`/platform/clinics/${clinic.id}/payment-method`),
    onSuccess: onChange,
  });

  if (config.isLoading) return <Spinner />;
  if (!config.data?.stripeConfigured) {
    return (
      <p className="text-xs text-ink-muted">
        Add Stripe keys on the server to save cards. Usage is still tracked meanwhile.
      </p>
    );
  }

  if (clinic.card && !editing) {
    return (
      <div>
        <p className="text-sm text-ink">
          {clinic.card.brand} ···· {clinic.card.last4}
          <span className="text-ink-muted">
            {' '}
            · exp {clinic.card.expMonth}/{clinic.card.expYear}
          </span>
        </p>
        <div className="mt-2 flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
            Replace card
          </Button>
          <Button size="sm" variant="ghost" onClick={() => remove.mutate()} loading={remove.isPending}>
            Remove
          </Button>
        </div>
      </div>
    );
  }

  return (
    <CardForm
      clinicId={clinic.id}
      publishableKey={config.data.publishableKey ?? ''}
      onSaved={() => {
        setEditing(false);
        onChange();
      }}
      onCancel={clinic.card ? () => setEditing(false) : undefined}
    />
  );
}

function CardForm({
  clinicId,
  publishableKey,
  onSaved,
  onCancel,
}: {
  clinicId: string;
  publishableKey: string;
  onSaved: () => void;
  onCancel?: () => void;
}) {
  const mountRef = useRef<HTMLDivElement>(null);
  const stripeRef = useRef<StripeJs | null>(null);
  // The Stripe card Element instance (loosely typed, see lib/stripe).
  type CardElement = ReturnType<ReturnType<StripeJs['elements']>['create']>;
  const cardRef = useRef<CardElement | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let cardEl: { unmount: () => void } | null = null;
    getStripe(publishableKey)
      .then((stripe) => {
        if (cancelled || !mountRef.current) return;
        stripeRef.current = stripe;
        const elements = stripe.elements();
        const el = elements.create('card', { style: { base: { fontSize: '14px' } } });
        el.mount(mountRef.current);
        el.on('change', (e) => setError(e.error?.message ?? null));
        cardRef.current = el;
        cardEl = el;
        setReady(true);
      })
      .catch(() => setError('Could not load Stripe.js. Check the connection and try again.'));
    return () => {
      cancelled = true;
      try {
        cardEl?.unmount();
      } catch {
        /* ignore */
      }
    };
  }, [publishableKey]);

  async function submit() {
    const stripe = stripeRef.current;
    const card = cardRef.current;
    if (!stripe || !card) return;
    setSaving(true);
    setError(null);
    try {
      const { clientSecret } = await api.post<{ clientSecret: string }>(
        `/platform/clinics/${clinicId}/setup-intent`,
      );
      const result = await stripe.confirmCardSetup(clientSecret, { payment_method: { card } });
      if (result.error) {
        setError(result.error.message ?? 'The card could not be saved.');
        return;
      }
      const pmId = result.setupIntent?.payment_method;
      if (!pmId) {
        setError('Stripe did not return a payment method.');
        return;
      }
      await api.post(`/platform/clinics/${clinicId}/payment-method`, { paymentMethodId: pmId });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the card.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div ref={mountRef} className="rounded-sm2 border border-ink/10 px-3 py-2.5 bg-white" />
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
      <div className="mt-3 flex gap-2">
        <Button size="sm" onClick={submit} loading={saving} disabled={!ready}>
          Save card
        </Button>
        {onCancel && (
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
      <p className="mt-2 text-[11px] text-ink-muted">
        Card details are sent directly to Stripe and never stored on MCOS servers.
      </p>
    </div>
  );
}
